import { type FC, useEffect, useMemo, useState } from 'react';
import { cn } from '@components/lib/utils';
import { Show } from '@components/Show';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import type { EvalBudgetStatus } from '@queries/evalRuns';
import { LoaderCircle } from 'lucide-react';

import type { EvalConfigOption } from '../types';

interface StartEvalRunFormProps {
  configs: EvalConfigOption[];
  isLoading: boolean;
  isStarting: boolean;
  /**
   * Today's token budget. When exhausted the button is disabled and the
   * reason shown; `undefined` (loading / fetch failed) never blocks — the
   * API enforces the cap anyway and answers with a 429.
   */
  budget?: EvalBudgetStatus;
  /** Button label — "Run" before the first scan, "Run new scan" in the admin footer. */
  label?: string;
  /**
   * Admin tools: choose any config and version. Off (the default, and what a
   * broker gets) the button runs the champion config at its current version.
   */
  showConfigPicker?: boolean;
  /**
   * The champion config the picker-less button runs; found in `configs` when
   * omitted. A broker has neither — they cannot read the config list — and
   * starts with no id at all: the server picks the champion.
   */
  championConfigId?: string;
  /**
   * Why the panel has decided a scan cannot run (contract stage, validation,
   * budget). Disables the button. The intro panel explains it in its own
   * alert; with the picker on (admin footer) the form shows it as a caption.
   * The server enforces the same rule, so there is no bypass.
   */
  disabledReason?: string;
  onStart: (evalConfigId?: string, evalConfigVersionNumber?: number) => void;
}

const formatTokens = (n: number): string => `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;

/**
 * One sentence on why the run button is off. The tenant message comes
 * first when both caps are gone — waiting for your own reset won't help.
 */
export const describeExhaustedBudget = (budget: EvalBudgetStatus): string => {
  const counter = budget.exhausted_scope === 'tenant' ? budget.tenant : budget.user;
  const who = budget.exhausted_scope === 'tenant' ? "This workspace's" : 'Your';
  return `${who} daily evaluation allowance is used up (${formatTokens(counter.used_tokens)} of ${formatTokens(counter.limit_tokens)} tokens). It resets at midnight UTC.`;
};

const selectClasses = cn(
  'h-8 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-700',
  'focus:border-primary-500 focus:ring-primary-500 focus:ring-1 focus:outline-none',
  'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500',
);

/**
 * The "run a scan" control in a panel footer. Brokers get one button that
 * runs the champion config. The admin tools add a compact config + version
 * picker above it (champion preselected, current version by default) so
 * staff can re-run an older config or version.
 */
export const StartEvalRunForm: FC<StartEvalRunFormProps> = ({
  configs,
  isLoading,
  isStarting,
  budget,
  label = 'Run guidance scan',
  showConfigPicker = false,
  championConfigId,
  disabledReason,
  onStart,
}) => {
  const champion = championConfigId ?? configs.find((c) => c.is_champion)?.id;

  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);

  const selectedConfig = useMemo(() => configs.find((c) => c.id === selectedId), [configs, selectedId]);
  const currentVersion = selectedConfig?.current_version_number;

  // Descending version list [current, current-1, ..., 1] so the latest
  // version appears first.
  const availableVersions = useMemo<number[]>(() => {
    if (!currentVersion || currentVersion < 1) return [];
    return Array.from({ length: currentVersion }, (_, i) => currentVersion - i);
  }, [currentVersion]);

  useEffect(() => {
    if (!selectedId && configs.length > 0) {
      setSelectedId(champion ?? configs[0].id);
    }
  }, [champion, configs, selectedId]);

  // Reset the version whenever the selected config changes, so we never send
  // a version that doesn't belong to it.
  useEffect(() => {
    setSelectedVersion(currentVersion);
  }, [currentVersion]);

  // Computed up front rather than inside `<Show>`: JSX children evaluate
  // eagerly, so the message must already be safe when `budget` is undefined.
  const budgetMessage = budget?.exhausted ? describeExhaustedBudget(budget) : undefined;

  // With the picker off the button never needs a config id: the server runs
  // the champion. The one case worth stopping client-side is staff who have
  // the config list and can see none is champion — a broker never loads the
  // list, so an empty one says nothing and the server's 400 is the answer.
  const championKnownMissing = !showConfigPicker && !isLoading && configs.length > 0 && !champion;
  const hasTarget = showConfigPicker ? Boolean(selectedId) && Boolean(selectedVersion) : !championKnownMissing;
  const isBlocked = Boolean(disabledReason);
  const canStart = hasTarget && !isStarting && !isLoading && budgetMessage === undefined && !isBlocked;

  const handleStart = () => {
    if (!showConfigPicker) {
      onStart(champion);
      return;
    }
    if (!selectedId) return;
    // Omit the version when it is the current published one — the backend
    // defaults to it.
    if (!selectedVersion || selectedVersion === currentVersion) {
      onStart(selectedId);
      return;
    }
    onStart(selectedId, selectedVersion);
  };

  if (showConfigPicker && !isLoading && configs.length === 0) {
    return (
      <p className="text-xs text-gray-500" data-testid="pbqa-no-configs">
        No published eval configurations are available. Publish a config in Settings → Eval Configs first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pbqa-start-run-form">
      <Show when={showConfigPicker && isLoading}>
        <div className="h-8 animate-pulse rounded-md bg-gray-100" />
      </Show>
      <Show when={showConfigPicker && !isLoading}>
        <div className="flex gap-2">
          <select
            id="pbqa-eval-config"
            aria-label="Eval configuration"
            data-testid="pbqa-eval-config-select"
            className={cn(selectClasses, 'min-w-0 flex-1')}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={isStarting}
          >
            {configs.map((config) => (
              <option key={config.id} value={config.id}>
                {/* Marks the one config brokers actually see runs of. */}
                {config.name}
                {config.is_champion ? ' · champion' : ''}
              </option>
            ))}
          </select>
          <Show when={availableVersions.length > 0}>
            <select
              id="pbqa-eval-config-version"
              aria-label="Version"
              data-testid="pbqa-eval-config-version-select"
              className={cn(selectClasses, 'w-28 shrink-0')}
              value={selectedVersion ?? ''}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
              disabled={isStarting || availableVersions.length <= 1}
            >
              {availableVersions.map((v) => (
                <option key={v} value={v}>
                  v{v}
                  {v === currentVersion ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </Show>
        </div>
      </Show>

      <Show when={showConfigPicker && isBlocked}>
        <p className="text-xs text-gray-500" data-testid="pbqa-start-blocked">
          {disabledReason}
        </p>
      </Show>

      <Show when={championKnownMissing}>
        <p className="text-xs text-gray-500" data-testid="pbqa-no-champion">
          No guidance configuration has been set up for this workspace yet.
        </p>
      </Show>

      {/* With the picker off, the intro panel carries the budget message in its own words. */}
      <Show when={showConfigPicker && budgetMessage !== undefined}>
        <Alert variant="warning" data-testid="pbqa-budget-exhausted">
          <AlertDescription>{budgetMessage}</AlertDescription>
        </Alert>
      </Show>

      <Button
        size="lg"
        className="w-full"
        data-testid="pbqa-start-run-button"
        disabled={!canStart}
        onClick={handleStart}
      >
        <Show when={isStarting}>
          <LoaderCircle className="animate-spin" aria-hidden />
        </Show>
        {label}
      </Button>
    </div>
  );
};
